import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  message,
  Tooltip,
  Popconfirm,
  Card,
  Statistic,
  Row,
  Col,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  getSsoIntegrations,
  deleteSsoIntegration,
  testSsoConnection,
  SsoIntegration,
} from '../lib/integrationsApi';
import CreateIntegrationDrawer from '../components/Integrations/CreateIntegrationModal';
import EditIntegrationDrawer from '../components/Integrations/EditIntegrationModal';

const IntegrationsPage: React.FC = () => {
  const [integrations, setIntegrations] = useState<SsoIntegration[]>([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<SsoIntegration | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    setLoading(true);
    try {
      const data = await getSsoIntegrations();
      setIntegrations(data);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteSsoIntegration(id);
      message.success('Integration deleted successfully');
      loadIntegrations();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete integration');
    }
  };

  const handleTest = async (id: string) => {
    setTestingId(id);
    try {
      const result = await testSsoConnection(id);
      if (result.success) {
        message.success(result.message);
      } else {
        message.error(result.message);
        if (result.errors) {
          result.errors.forEach(err => message.warning(err));
        }
      }
    } catch (error: any) {
      message.error('Failed to test connection');
    } finally {
      setTestingId(null);
    }
  };

  const getProviderColor = (provider: string): string => {
    const colors: Record<string, string> = {
      teams: 'purple',
      maximo: 'orange',
      slack: 'magenta',
      'custom-sso': 'cyan',
    };
    return colors[provider] || 'default';
  };

  const getProviderLabel = (provider: string): string => {
    const labels: Record<string, string> = {
      teams: 'Microsoft Teams',
      maximo: 'IBM Maximo',
      slack: 'Slack',
      'custom-sso': 'Custom SSO',
    };
    return labels[provider] || provider;
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: SsoIntegration) => (
        <div>
          <div className="font-medium">{text}</div>
          {record.description && (
            <div className="text-xs text-gray-500">{record.description}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      render: (provider: string) => (
        <Tag color={getProviderColor(provider)}>{getProviderLabel(provider)}</Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag
          icon={isActive ? <CheckCircleOutlined /> : <ExclamationCircleOutlined />}
          color={isActive ? 'success' : 'default'}
        >
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Widgets Using',
      dataIndex: 'widgetCount',
      key: 'widgetCount',
      align: 'center' as const,
      render: (count: number) => <span className="font-semibold">{count || 0}</span>,
    },
    {
      title: 'Usage Count',
      dataIndex: 'usageCount',
      key: 'usageCount',
      align: 'center' as const,
    },
    {
      title: 'Last Used',
      dataIndex: 'lastUsedAt',
      key: 'lastUsedAt',
      render: (date: string) => (date ? new Date(date).toLocaleString() : 'Never'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: SsoIntegration) => (
        <Space>
          <Tooltip title="Test Connection">
            <Button
              size="small"
              icon={<ThunderboltOutlined />}
              onClick={() => handleTest(record.id)}
              loading={testingId === record.id}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedIntegration(record);
                setEditModalVisible(true);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Integration?"
            description={
              record.widgetCount && record.widgetCount > 0
                ? `This integration is used by ${record.widgetCount} widget(s). Are you sure you want to delete it?`
                : 'Are you sure you want to delete this integration?'
            }
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button size="small" icon={<DeleteOutlined />} danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const activeIntegrations = integrations.filter((i) => i.isActive).length;
  const totalWidgets = integrations.reduce((sum, i) => sum + (i.widgetCount || 0), 0);

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold mb-1">SSO Integrations</h1>
            <p className="text-gray-600">
              Configure authentication providers for your chat widgets
            </p>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreateModalVisible(true)}
          >
            New Integration
          </Button>
        </div>

        <Row gutter={16} className="mb-6">
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Integrations"
                value={integrations.length}
                valueStyle={{ color: '#13afe3' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Active Integrations"
                value={activeIntegrations}
                valueStyle={{ color: '#10B981' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Widgets Using Integrations"
                value={totalWidgets}
                valueStyle={{ color: '#6366f1' }}
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Usage"
                value={integrations.reduce((sum, i) => sum + i.usageCount, 0)}
                valueStyle={{ color: '#F59E0B' }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      <Table
        columns={columns}
        dataSource={integrations}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} integrations`,
        }}
      />

      <CreateIntegrationDrawer
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        onSuccess={() => {
          loadIntegrations();
        }}
      />

      {selectedIntegration && (
        <EditIntegrationDrawer
          visible={editModalVisible}
          integration={selectedIntegration}
          onClose={() => {
            setEditModalVisible(false);
            setSelectedIntegration(null);
          }}
          onSuccess={() => {
            setEditModalVisible(false);
            setSelectedIntegration(null);
            loadIntegrations();
          }}
        />
      )}
    </div>
  );
};

export default IntegrationsPage;
